import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';

import { i18Service } from '../../services/i18service';
import { LoginService } from '../../components/Authentication/auth/login.service';
import { LogoutService } from '../../components/Authentication/auth/logout.service';
import { AccountTypeService } from '../../services/account-type';
import { UserService } from '../../services/user';
import { PointOfSaleService } from '../../services/pos';
import { PetsService } from '../../services/pets';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  accessPage?: string;
  alwaysVisible?: boolean;
  hideIfAffiliation?: boolean;
  onlyForAffiliation?: boolean;
  requiresRoleCheck?: () => boolean;
  showUpcomingBadge?: boolean;
}

@Component({
  selector: 'app-header-mobile',
  templateUrl: './header.mobile.component.html',
  styleUrl: './header.mobile.component.scss'
})
export class HeaderMobileComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentPosId: number | null = null;
  private activeLoadingRequests = 0;

  contentReady = false;
  openMenu = false;
  tokenExists = false;

  cartItems: any[] = [];
  cartItemCount = 0;
  cartPopUp = false;
  showCart = true;
  languageDropdownOpen = false;

  menuReady = true;
  hasPOSData = false;
  upcoming = 0;

  warehouseType: number | null = null;

  constructor(
    private i18: i18Service,
    private loginService: LoginService,
    private logoutService: LogoutService,
    private location: Location,
    public accountService: AccountTypeService,
    private userService: UserService,
    private posService: PointOfSaleService,
    private petsService: PetsService,
    private router: Router
  ) {
    this.i18.init();
  }

  get isAffiliation(): boolean {
    return this.accountService.isAffiliation();
  }

  /* =========================
        MENU CONFIGS MOBILE
  ========================== */

  private companyMenu: MenuItem[] = [
    { label: 'Pospage', icon: 'fa-solid fa-store', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'Stock', icon: 'fa-solid fa-boxes-stacked', route: '/Home.mobile', accessPage: '6' },
    { label: 'Orders', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'supplies', icon: 'fa-solid fa-truck', route: '/suppliespage.mobile', accessPage: '4' },
    { label: 'delivery', icon: 'fa-solid fa-truck-fast', route: '/deliverymanager.mobile', accessPage: '3' },
    { label: 'myclients', icon: 'fa-solid fa-address-book', route: '/myclients.mobile', accessPage: '2' },
    { label: 'financial', icon: 'fa-solid fa-wallet', route: '/dailyexpenses.mobile', accessPage: '1' },
    { label: 'employees', icon: 'fa-solid fa-id-badge', route: '/employeepage.mobile', accessPage: '0' },
    { label: 'Users', icon: 'fa-solid fa-users-gear', route: '/allusers.mobile', requiresRoleCheck: () => this.accountService.canManageUsers() },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'income', icon: 'fa-solid fa-chart-line', route: '/financial-manager.mobile', onlyForAffiliation: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  private warehouseMenu: MenuItem[] = [
    { label: 'Pospage', icon: 'fa-solid fa-store', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'Stock', icon: 'fa-solid fa-boxes-stacked', route: '/Home.mobile', accessPage: '6' },
    { label: 'Orders', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'supplies', icon: 'fa-solid fa-truck', route: '/suppliespage.mobile', accessPage: '4' },
    { label: 'delivery', icon: 'fa-solid fa-truck-fast', route: '/deliverymanager.mobile', accessPage: '3' },
    { label: 'myclients', icon: 'fa-solid fa-address-book', route: '/myclients.mobile', accessPage: '2' },
    { label: 'financial', icon: 'fa-solid fa-wallet', route: '/dailyexpenses.mobile', accessPage: '1' },
    { label: 'employees', icon: 'fa-solid fa-id-badge', route: '/employeepage.mobile', accessPage: '0' },
    { label: 'Users', icon: 'fa-solid fa-users-gear', route: '/allusers.mobile', requiresRoleCheck: () => this.accountService.canManageUsers() },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  private shopMenu: MenuItem[] = [
    { label: 'Pospage', icon: 'fa-solid fa-store', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'products', icon: 'fa-solid fa-boxes-stacked', route: '/Home.mobile', accessPage: '6' },
    { label: 'clinic', icon: 'fa-solid fa-calendar-days', route: '/Clinic.mobile', accessPage: '2' },
    { label: 'all services', icon: 'fa-solid fa-stethoscope', route: '/AllServices.mobile', accessPage: '7' },
    { label: 'client pets', icon: 'fa-solid fa-paw', route: '/pets.mobile', accessPage: '2' },
    { label: 'myclients', icon: 'fa-solid fa-address-book', route: '/myclients.mobile', accessPage: '2' },
    { label: 'invoices', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'supplies', icon: 'fa-solid fa-truck', route: '/suppliespage.mobile', accessPage: '4' },
    { label: 'financial', icon: 'fa-solid fa-wallet', route: '/dailyexpenses.mobile', accessPage: '1' },
    { label: 'employees', icon: 'fa-solid fa-id-badge', route: '/employeepage.mobile', accessPage: '0' },
    { label: 'Users', icon: 'fa-solid fa-users-gear', route: '/allusers.mobile', requiresRoleCheck: () => this.accountService.canManageUsers() },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  private clinicMenu: MenuItem[] = [
    { label: 'Pospage', icon: 'fa-solid fa-store', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'clinic', icon: 'fa-solid fa-calendar-days', route: '/Clinic.mobile', accessPage: '2' },
    { label: 'all services', icon: 'fa-solid fa-stethoscope', route: '/AllServices.mobile', accessPage: '7' },
    { label: 'client pets', icon: 'fa-solid fa-paw', route: '/pets.mobile', accessPage: '2', showUpcomingBadge: true },
    { label: 'myclients', icon: 'fa-solid fa-address-book', route: '/myclients.mobile', accessPage: '2' },
    { label: 'invoices', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'financial', icon: 'fa-solid fa-wallet', route: '/dailyexpenses.mobile', accessPage: '1' },
    { label: 'employees', icon: 'fa-solid fa-id-badge', route: '/employeepage.mobile', accessPage: '0' },
    { label: 'Users', icon: 'fa-solid fa-users-gear', route: '/allusers.mobile', requiresRoleCheck: () => this.accountService.canManageUsers() },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  private shopAndClinicMenu: MenuItem[] = [
    { label: 'Pospage', icon: 'fa-solid fa-store', route: '/Pospage.mobile', accessPage: '7' },
    { label: 'products', icon: 'fa-solid fa-boxes-stacked', route: '/Home.mobile', accessPage: '6' },
    { label: 'clinic', icon: 'fa-solid fa-calendar-days', route: '/Clinic.mobile', accessPage: '2' },
    { label: 'all services', icon: 'fa-solid fa-stethoscope', route: '/AllServices.mobile', accessPage: '7' },
    { label: 'client pets', icon: 'fa-solid fa-paw', route: '/pets.mobile', accessPage: '2', showUpcomingBadge: true },
    { label: 'clientsbookings', icon: 'fa-solid fa-calendar-check', route: '/clientsbookings.mobile', accessPage: '2' },
    { label: 'myclients', icon: 'fa-solid fa-address-book', route: '/myclients.mobile', accessPage: '2' },
    { label: 'invoices', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'supplies', icon: 'fa-solid fa-truck', route: '/suppliespage.mobile', accessPage: '4' },
    { label: 'financial', icon: 'fa-solid fa-wallet', route: '/dailyexpenses.mobile', accessPage: '1' },
    { label: 'employees', icon: 'fa-solid fa-id-badge', route: '/employeepage.mobile', accessPage: '0' },
    { label: 'Users', icon: 'fa-solid fa-users-gear', route: '/allusers.mobile', requiresRoleCheck: () => this.accountService.canManageUsers() },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  private shippingMenu: MenuItem[] = [
    { label: 'Orders', icon: 'fa-solid fa-receipt', route: '/orders.mobile', accessPage: '5' },
    { label: 'delivery', icon: 'fa-solid fa-truck-fast', route: '/deliverymanager.mobile', accessPage: '3' },
    { label: 'Profile', icon: 'fa-solid fa-circle-user', route: '/Profile', alwaysVisible: true },
    { label: 'fingerprint', icon: 'fa-solid fa-fingerprint', route: '/fingerprint.mobile', alwaysVisible: true }
  ];

  /* =========================
        ACTIVE MENU
  ========================== */

  private get activeMenuConfig(): MenuItem[] {
    if (this.isShopAndClinicType()) return this.shopAndClinicMenu;
    if (this.isClinicType()) return this.clinicMenu;
    if (this.isShopType()) return this.shopMenu;
    if (this.isWarehouseType()) return this.warehouseMenu;
    if (this.isShippingType()) return this.shippingMenu;
    if (this.isCompanyType()) return this.companyMenu;

    return this.companyMenu;
  }

 get menuItems(): MenuItem[] {
    return this.activeMenuConfig.filter(item => {
      if (item.onlyForAffiliation) {
        return this.isAffiliation;
      }

      if (item.hideIfAffiliation && this.isAffiliation) {
        return false;
      }

      if (item.requiresRoleCheck) {
        return item.requiresRoleCheck();
      }

      if (item.alwaysVisible) {
        return true;
      }

      if (!item.accessPage) {
        return false;
      }

      return this.accountService.hasAccess(item.accessPage);
    });
  }



  /* =========================
        TYPE HELPERS
  ========================== */

  private serviceFlag(methodName: string): boolean {
    const serviceAsAny = this.accountService as any;
    const fn = serviceAsAny?.[methodName];

    return typeof fn === 'function' ? !!fn.call(this.accountService) : false;
  }

  private hasWarehouseType(): boolean {
    return this.warehouseType !== null && !Number.isNaN(this.warehouseType);
  }

  private isCompanyType(): boolean {
    return !this.hasWarehouseType() && this.serviceFlag('isCompany');
  }

  private isShopAndClinicType(): boolean {
    if (this.hasWarehouseType()) return this.warehouseType === 4;
    return this.serviceFlag('isShopAndClinic');
  }

  private isWarehouseType(): boolean {
    if (this.hasWarehouseType()) return this.warehouseType === 1;
    return this.serviceFlag('isWarehouse');
  }

  private isShopType(): boolean {
    if (this.hasWarehouseType()) return this.warehouseType === 2;
    return this.serviceFlag('isShop');
  }

  private isClinicType(): boolean {
    if (this.hasWarehouseType()) return this.warehouseType === 3;
    return this.serviceFlag('isClinic');
  }

  private isShippingType(): boolean {
    if (this.hasWarehouseType()) return this.warehouseType === 5;
    return this.serviceFlag('isShipping');
  }

  /* =========================
        LIFECYCLE
  ========================== */

  ngOnInit(): void {
    this.loginService.token$
      .pipe(takeUntil(this.destroy$))
      .subscribe(token => {
        this.tokenExists = !!token;

        if (token) {
          this.refreshHeaderState(true);
        } else {
          this.resetHeaderState();
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.openMenu = false;
        this.cartPopUp = false;
        this.languageDropdownOpen = false;

        this.updateCart();
        this.updateCartVisibility();

        // Re-check posId after route changes because warehouse context may change without refreshing.
        this.refreshHeaderState();
      });

    this.updateCart();
    this.updateCartVisibility();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showLoader(): void {
    this.activeLoadingRequests++;
    this.contentReady = true;
  }

  private hideLoader(): void {
    this.activeLoadingRequests = Math.max(this.activeLoadingRequests - 1, 0);
    this.contentReady = this.activeLoadingRequests > 0;
  }

  private resetLoader(): void {
    this.activeLoadingRequests = 0;
    this.contentReady = false;
  }

  @HostListener('window:warehouse-context-changed')
  onWarehouseContextChanged(): void {
    this.refreshHeaderState(true);
  }

  @HostListener('window:storage', ['$event'])
  onStorageChanged(event: StorageEvent): void {
    if (event.key === 'posId') {
      this.refreshHeaderState(true);
    }

    if (event.key === 'cart') {
      this.updateCart();
    }
  }

  private resetHeaderState(): void {
    this.openMenu = false;
    this.cartPopUp = false;
    this.cartItems = [];
    this.cartItemCount = 0;
    this.hasPOSData = false;
    this.upcoming = 0;
    this.warehouseType = null;
    this.currentPosId = null;
    this.resetLoader();
    this.menuReady = true;
  }

  private getCurrentPosId(): number | null {
    const raw = localStorage.getItem('posId');
    const id = raw ? Number(raw) : null;

    return id && !Number.isNaN(id) ? id : null;
  }

  private refreshHeaderState(force = false): void {
    if (!this.tokenExists) {
      this.resetHeaderState();
      return;
    }

    const posId = this.getCurrentPosId();

    if (!force && posId === this.currentPosId) {
      return;
    }

    this.currentPosId = posId;
    this.menuReady = false;
    this.showLoader();

    this.accountService.getAccountType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.initializeUserState();
          this.loadWarehouseType(posId, true);
          this.loadUpcoming();
        },
        error: (err) => {
          console.error('Failed to refresh header state', err);
          this.menuReady = true;
          this.hideLoader();
        }
      });
  }

  private initializeUserState(): void {
    if (this.accountService.isSuperAdmin()) {
      this.hasPOSData = true;
      return;
    }

    this.userService.getUserId()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.getUserWarehouse(data.user_id);
        },
        error: (err) => console.error(err)
      });
  }

  private getUserWarehouse(userId: string): void {
    this.posService.getUserPOS(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          const list = Array.isArray(data)
            ? data
            : (Array.isArray(data?.data) ? data.data : []);

          this.hasPOSData = list.length > 0;

          if (!this.hasPOSData && this.router.url.includes('/Pospage')) {
            if (this.accountService.isAffiliation()) {
              this.router.navigate(['/Home.mobile']);
            } else {
              this.router.navigate(['/fingerprint.mobile']);
            }
          }
        },
        error: (err) => console.error(err)
      });
  }

  private loadWarehouseType(posId: number | null = this.getCurrentPosId(), hideMainLoader = false): void {
    if (!posId) {
      this.warehouseType = null;
      this.menuReady = true;

      if (hideMainLoader) {
        this.hideLoader();
      }

      return;
    }

    const requestedPosId = posId;

    this.userService.getWarehouseType(posId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ warehouses_type }) => {
          // Prevent old API responses from overwriting the current warehouse type.
          if (requestedPosId !== this.currentPosId) {
            if (hideMainLoader) {
              this.hideLoader();
            }

            return;
          }

          const type = Number(warehouses_type);
          this.warehouseType = Number.isFinite(type) ? type : null;
          this.menuReady = true;

          if (hideMainLoader) {
            this.hideLoader();
          }
        },
        error: (err) => {
          if (requestedPosId !== this.currentPosId) {
            if (hideMainLoader) {
              this.hideLoader();
            }

            return;
          }

          console.error(err);
          this.warehouseType = null;
          this.menuReady = true;

          if (hideMainLoader) {
            this.hideLoader();
          }
        }
      });
  }

  loadUpcoming(): void {
    const warehouseId = Number(localStorage.getItem('posId'));
    if (!warehouseId) {
      this.upcoming = 0;
      return;
    }

    this.petsService.getUpcomingVaccinations(warehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.upcoming = Number(res?.count || 0);
        },
        error: () => {
          this.upcoming = 0;
        }
      });
  }

  /* =========================
        MENU
  ========================== */

  openMenuToggle(): void {
    if (!this.openMenu) {
      this.refreshHeaderState(true);
    }

    this.openMenu = !this.openMenu;
    this.cartPopUp = false;
    this.languageDropdownOpen = false;
  }

  close(): void {
    this.openMenu = false;
    this.languageDropdownOpen = false;
  }

  /* =========================
        CART
  ========================== */

  updateCart(): void {
    const currentWarehouseId = localStorage.getItem('posId');
    const cartWarehouseId = localStorage.getItem('cartWarehouseId');

    if (cartWarehouseId && currentWarehouseId && cartWarehouseId !== currentWarehouseId) {
      this.clearCartStorage();
      this.cartItems = [];
      this.cartItemCount = 0;
      return;
    }

    const storedCart = localStorage.getItem('cart');

    try {
      this.cartItems = storedCart ? JSON.parse(storedCart) : [];
    } catch {
      this.cartItems = [];
      this.clearCartStorage();
    }

    this.cartItemCount = this.cartItems.length;
  }

  updateCartVisibility(): void {
    const path = this.location.path().toLowerCase();

    this.showCart =
      !path.includes('/addorder') &&
      !path.includes('/home');
  }

  cartPopUpOpen(): void {
    this.openMenu = false;
    this.cartPopUp = true;
  }

  closeCart(): void {
    this.cartPopUp = false;
  }

  removeFromCart(item: any): void {
    this.cartItems = this.cartItems.filter(i => i.id !== item.id);
    this.saveCartStorage();

    this.updateCart();

    if (!this.cartItems.length) {
      this.cartPopUp = false;
    }
  }

  private saveCartStorage(): void {
    if (!this.cartItems.length) {
      this.clearCartStorage();
      return;
    }

    const currentWarehouseId = localStorage.getItem('posId');
    localStorage.setItem('cart', JSON.stringify(this.cartItems));

    if (currentWarehouseId) {
      localStorage.setItem('cartWarehouseId', currentWarehouseId);
    }
  }

  private clearCartStorage(): void {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartWarehouseId');
  }

  quickOrder(): void {
    const posId = this.getCurrentPosId();

    if (!posId) {
      this.router.navigate(['/Addorder'], {
        queryParams: {
          cart: JSON.stringify(this.cartItems)
        }
      });
      return;
    }

    this.userService.getWarehouseType(posId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ warehouses_type }) => {
          const type = Number(warehouses_type);

          this.cartPopUp = false;
          this.openMenu = false;

          if ([2, 3, 4].includes(type)) {
            this.router.navigate(['/Addorderstore'], {
              queryParams: {
                cart: JSON.stringify(this.cartItems)
              }
            });
            return;
          }

          if (type === 1) {
            this.router.navigate(['/Addorderwarehouse'], {
              queryParams: {
                cart: JSON.stringify(this.cartItems)
              }
            });
            return;
          }

          this.router.navigate(['/Addorder'], {
            queryParams: {
              cart: JSON.stringify(this.cartItems)
            }
          });
        },
        error: (err) => console.error(err)
      });
  }

  /* =========================
        LANGUAGE
  ========================== */

  get currentLanguage(): string {
    return localStorage.getItem('language') || 'en';
  }

  toggleLanguageDropdown(event?: Event): void {
    event?.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  setLanguage(lang: string, event?: Event) {
    event?.stopPropagation();
    this.i18.setLanguage(lang);
    this.languageDropdownOpen = false;
    this.close();
  }

  toggleLanguage() {
    this.i18.toggleLanguage();
    this.close();
  }

  /* =========================
        LOGOUT
  ========================== */

  logout(): void {
    this.close();

    this.logoutService.logout(localStorage.getItem('token'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        localStorage.removeItem('username');
        localStorage.removeItem('user_type');
        localStorage.removeItem('access_pages');
        localStorage.removeItem('cart');
        localStorage.removeItem('cartWarehouseId');
        localStorage.removeItem('token');

        window.location.reload();
      });
  }
}
